import { readCookie } from "modules/cookieManager";
import { camelCaseObjectKeys } from "utils/camelCase";

/**
 * Retrieves location parameters from the user's cookie.
 *
 * This function reads the 'Personal' cookie, parses it, and extracts the 
 * `marketId` and `marketStateAbbreviation` values. If the cookie or values 
 * are not present, it defaults to `0` for `marketId` and an empty string for 
 * `marketStateAbbreviation`.
 *
 * @returns {Object} An object containing `marketId` and `marketStateAbbreviation`.
 *
 * @example
 * const locationParams = getArticleLocationParameters();
 * console.log(locationParams); // { marketId: 123, marketStateAbbreviation: 'CA' }
 */
const getArticleLocationParameters = function() {
  const personalCookie = readCookie('Personal', true);
  const parameters = new URLSearchParams(personalCookie);

  return {
    marketId: parameters.get('market') || 0,
    marketStateAbbreviation: parameters.get('state') || ''
  };
};

/**
 * Retrieves search parameters from a specific element.
 *
 * This function extracts the inner text of the provided element, parses it as 
 * JSON, and converts the keys to camelCase using `camelCaseObjectKeys`.
 *
 * @param {HTMLElement} articleSearchParametersElement - The element containing the search parameters.
 * @returns {Object|null} An object containing the parsed search parameters, or null if not found.
 *
 * @example
 * const element = document.querySelector('[data-article-search-parameters]');
 * const searchParams = getArticleSearchParameters(element);
 * console.log(searchParams); // { searchTerm: 'example', page: 1 }
 */
const getArticleSearchParameters = function(articleSearchParametersElement) {
  const searchParametersData = articleSearchParametersElement.innerText || null;
  return searchParametersData ? camelCaseObjectKeys(JSON.parse(searchParametersData)) : null;
};

/**
 * Combines search parameters and location parameters into a single command.
 *
 * This function retrieves search parameters from the provided element and 
 * optionally includes location parameters from the user's cookie. If either 
 * set of parameters is present, it combines them into a single object.
 *
 * @param {HTMLElement} articleSearchParametersElement - The element containing the search parameters.
 * @returns {Object|null} An object containing the combined search and location parameters, or null if both are absent.
 *
 * @example
 * const element = document.querySelector('[data-article-search-parameters]');
 * const searchCommand = getArticlesSearchCommand(element);
 * console.log(searchCommand); 
 * // { searchTerm: 'example', page: 1, marketId: 123, marketStateAbbreviation: 'CA' }
 */
const getArticlesSearchCommand = function(articleSearchParametersElement) {
  if(!articleSearchParametersElement){
    return null;
  }

  const addLocationParameters = articleSearchParametersElement.hasAttribute('data-add-location-parameters');
  const articleLocationParameters = addLocationParameters ? getArticleLocationParameters() : null;
  const articleSearchParameters = getArticleSearchParameters(articleSearchParametersElement);

  return articleSearchParameters ? {
    ...articleSearchParameters,
    ...articleLocationParameters
  } : null;
};

export default getArticlesSearchCommand;